import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';
import { float, materialColor, mx_noise_float, positionLocal, smoothstep, vec3 } from 'three/tsl';
import * as THREE from 'three/webgpu';
import { placeBone, solveLimb } from '../../engine/character/limbIK';
import { bakeMeshes } from '../../engine/render/bakeMeshes';
import { BRUTE, CHARGER, CRITTER, type Kind, KNIGHT, RAIDER, SLIME } from './enemyKinds';
import { buildWeaponModel, type WeaponMaterials, weaponMaterials } from './weaponModels';
import { WEAPONS } from './weapons';

type N = any;

/**
 * The test enemies' models, built from code. Each stands in its body's frame: the origin at
 * the capsule's (or ball's) centre, +Y up, facing −Z, its feet at −height/2, and fits inside
 * the capsule more or less. All its materials share one glow (`glow`): the flash of a hit, a
 * stun's shimmer and a charge's warning light the whole figure.
 */
export interface EnemyModel {
  readonly group: THREE.Group;
  /** What turns with the shield (its arm too), for the shield's pivot; null without one. */
  readonly shield: THREE.Object3D | null;
  /** Per frame: idle motion, and a walk or scuttle at `speed` (m/s). */
  update(dt: number, speed: number): void;
}

/** How a surface looks close up (its grain), and its roughness and metalness. */
type Finish = 'skin' | 'cloth' | 'leather' | 'metal' | 'shell' | 'slime' | 'horn' | 'dark' | 'eye';

/** One material per finish and colour; the colour is a uniform, so a finish compiles once. */
function surface(color: THREE.ColorRepresentation, finish: Finish, glow: N): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ color });
  const p: N = positionLocal;
  const c: N = materialColor;
  switch (finish) {
    case 'skin':
      m.roughness = 0.62;
      m.colorNode = c.mul(mx_noise_float(p.mul(9)).mul(0.14).add(1));
      break;
    case 'cloth':
      m.roughness = 0.9;
      m.colorNode = c
        .mul(
          mx_noise_float(p.mul(vec3(160, 50, 160)))
            .mul(0.08)
            .add(1),
        )
        .mul(
          smoothstep(-0.6, 0.6, mx_noise_float(p.mul(vec3(7, 2, 7))))
            .mul(0.2)
            .add(0.88),
        );
      break;
    case 'leather':
      m.roughness = 0.72;
      m.colorNode = c.mul(
        smoothstep(-0.4, 0.6, mx_noise_float(p.mul(22)))
          .mul(0.3)
          .add(0.8),
      );
      break;
    case 'metal': {
      m.metalness = 0.65;
      const n = mx_noise_float(p.mul(40));
      m.colorNode = c.mul(n.mul(0.1).add(1));
      m.roughnessNode = float(0.38).add(n.mul(0.1));
      break;
    }
    case 'shell':
      m.roughness = 0.22;
      m.metalness = 0.35;
      m.colorNode = c.mul(mx_noise_float(p.mul(30)).mul(0.12).add(1));
      break;
    case 'slime':
      // Glossy, darker toward the bottom, mottled.
      m.roughness = 0.12;
      m.colorNode = c
        .mul(smoothstep(-0.6, 0.6, p.y).mul(0.5).add(0.65))
        .mul(mx_noise_float(p.mul(5)).mul(0.18).add(1));
      break;
    case 'horn':
      m.roughness = 0.45;
      m.colorNode = c.mul(
        mx_noise_float(p.mul(vec3(60, 6, 60)))
          .mul(0.12)
          .add(1),
      );
      break;
    case 'dark':
      m.roughness = 0.4;
      break;
    case 'eye':
      m.roughness = 0.3;
      // Eyes glow faintly of themselves.
      m.emissiveNode = c.mul(2.5).add(glow);
      return m;
  }
  m.emissiveNode = glow;
  return m;
}

/** A piece hanging from its parent's origin `len` m down -Y, radius r0 at the top to r1. */
function hang(
  parent: THREE.Object3D,
  len: number,
  r0: number,
  r1: number,
  mat: THREE.Material,
  seg = 10,
): THREE.Mesh {
  const g = new THREE.CylinderGeometry(r0, r1, len, seg);
  g.translate(0, -len / 2, 0);
  return add(parent, g, mat);
}

function add(
  parent: THREE.Object3D,
  geo: THREE.BufferGeometry,
  mat: THREE.Material,
  x = 0,
  y = 0,
  z = 0,
): THREE.Mesh {
  const m = new THREE.Mesh(geo, mat);
  m.position.set(x, y, z);
  parent.add(m);
  return m;
}

/** A body of revolution from (radius, height) pairs, squashed front to back by `depth`. */
function lathe(profile: [number, number][], depth: number, segments = 20): THREE.BufferGeometry {
  const g = new THREE.LatheGeometry(
    profile.map(([r, y]) => new THREE.Vector2(r, y)),
    segments,
  );
  g.scale(1, 1, depth);
  g.computeVertexNormals();
  return g;
}

/** A horn: a tube along `points` tapering from r0 to a point. */
function horn(points: THREE.Vector3[], r0: number): THREE.BufferGeometry {
  const curve = new THREE.CatmullRomCurve3(points);
  const tubular = 12;
  const radial = 8;
  const g = new THREE.TubeGeometry(curve, tubular, r0, radial, false);
  // Taper: pull each ring toward the curve, more toward the tip.
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  const c = new THREE.Vector3();
  const v = new THREE.Vector3();
  for (let i = 0; i <= tubular; i++) {
    const t = i / tubular;
    curve.getPointAt(t, c);
    const k = 1 - t * 0.92;
    for (let j = 0; j <= radial; j++) {
      const idx = i * (radial + 1) + j;
      v.fromBufferAttribute(pos, idx).sub(c).multiplyScalar(k).add(c);
      pos.setXYZ(idx, v.x, v.y, v.z);
    }
  }
  g.computeVertexNormals();
  return g;
}

const _s = new THREE.Vector3();
const _h = new THREE.Vector3();
const _e = new THREE.Vector3();
const _pole = new THREE.Vector3();

/**
 * An arm placed once, shoulder to hand (bending toward `pole`, a direction): upper arm, elbow,
 * forearm, and a fist (or what `hand` builds) at the end, turned along the forearm.
 */
function arm(
  parent: THREE.Object3D,
  shoulder: [number, number, number],
  hand: [number, number, number],
  upper: number,
  lower: number,
  pole: [number, number, number],
  r: [number, number, number],
  mats: { upper: THREE.Material; lower: THREE.Material; hand: THREE.Material },
): THREE.Object3D {
  _s.set(...shoulder);
  _h.set(...hand);
  solveLimb(_s, _h, upper, lower, _pole.set(...pole).add(_s), _e);
  const up = add(parent, new THREE.CylinderGeometry(r[1], r[0], 1, 10).translate(0, 0.5, 0), mats.upper);
  placeBone(up, _s, _e);
  add(parent, new THREE.SphereGeometry(r[1] * 1.05, 10, 8), mats.upper, _e.x, _e.y, _e.z);
  const fo = add(parent, new THREE.CylinderGeometry(r[2], r[1], 1, 10).translate(0, 0.5, 0), mats.lower);
  placeBone(fo, _e, _h);
  const end = new THREE.Group();
  end.position.copy(_h);
  end.quaternion.copy(fo.quaternion);
  add(end, new THREE.SphereGeometry(r[2] * 1.5, 10, 8), mats.hand).scale.set(1, 1.15, 0.9);
  parent.add(end);
  return end;
}

/** A leg that swings from the hip: thigh, knee, shin and a foot. */
function leg(
  parent: THREE.Object3D,
  x: number,
  hip: number,
  thigh: number,
  shin: number,
  r: [number, number, number],
  mats: { thigh: THREE.Material; shin: THREE.Material },
  foot: (at: THREE.Object3D) => void,
): THREE.Group {
  const g = new THREE.Group();
  g.position.set(x, hip, 0);
  parent.add(g);
  hang(g, thigh, r[0], r[1], mats.thigh);
  add(g, new THREE.SphereGeometry(r[1] * 1.08, 10, 8), mats.thigh, 0, -thigh, 0);
  const knee = new THREE.Group();
  knee.position.y = -thigh;
  g.add(knee);
  hang(knee, shin, r[1], r[2], mats.shin);
  const ankle = new THREE.Group();
  ankle.position.y = -shin;
  knee.add(ankle);
  foot(ankle);
  return g;
}

/** Legs and arms swinging in step at `speed`, and a little breathing. */
class Gait {
  private phase = 0;
  private stride = 0;
  private clock = Math.random() * 10;
  constructor(
    private readonly legs: THREE.Object3D[],
    private readonly body: THREE.Object3D | null,
    private readonly swing = 0.5,
    private readonly cadence = 1.6,
  ) {
    for (const l of legs) l.userData.moves = true;
    if (body) body.userData.moves = true;
  }

  update(dt: number, speed: number): void {
    this.clock += dt;
    this.stride += (Math.min(1, speed / 3) - this.stride) * (1 - Math.exp(-dt * 8));
    this.phase += dt * speed * this.cadence;
    for (const [i, l] of this.legs.entries()) {
      l.rotation.x = Math.sin(this.phase + (i % 2) * Math.PI) * this.swing * this.stride;
    }
    if (this.body) {
      this.body.position.y =
        Math.sin(this.clock * 1.6) * 0.008 + Math.abs(Math.cos(this.phase)) * 0.03 * this.stride;
    }
  }
}

let shared: WeaponMaterials | null = null;
/** The enemies' weapons use the player's weapons' materials (and don't flash with them). */
function weaponMats(): WeaponMaterials {
  shared ??= weaponMaterials();
  return shared;
}

/**
 * The model for `kind`, lit by `glow` (see the module's comment). Its rigid parts are merged
 * into a mesh per material (bakeMeshes): a few draws each rather than dozens.
 */
export function buildEnemyModel(kind: Kind, glow: N): EnemyModel {
  const model = buildParts(kind, glow);
  if (model.shield) model.shield.userData.moves = true;
  bakeMeshes(model.group);
  return model;
}

function buildParts(kind: Kind, glow: N): EnemyModel {
  switch (kind) {
    case SLIME:
      return slime(kind, glow);
    case CRITTER:
      return critter(kind, glow);
    case KNIGHT:
      return knight(kind, glow);
    case RAIDER:
      return raider(kind, glow);
    case CHARGER:
      return charger(kind, glow);
    case BRUTE:
      return brute(kind, glow);
    default:
      return blob(kind, glow);
  }
}

/** Any other kind: its capsule, with eyes. */
function blob(kind: Kind, glow: N): EnemyModel {
  const group = new THREE.Group();
  const ball = kind.height <= 2 * kind.radius + 1e-3;
  add(
    group,
    ball
      ? new THREE.SphereGeometry(kind.radius, 20, 14)
      : new THREE.CapsuleGeometry(kind.radius, kind.height - 2 * kind.radius, 6, 16),
    surface(kind.color, 'skin', glow),
  );
  const eye = surface(0xffd27a, 'eye', glow);
  for (const s of [-1, 1]) {
    add(
      group,
      new THREE.SphereGeometry(kind.radius * 0.12, 8, 6),
      eye,
      s * kind.radius * 0.35,
      kind.height / 2 - kind.radius * 0.8,
      -kind.radius * 0.92,
    );
  }
  return { group, shield: null, update: () => {} };
}

/** A glossy green blob with big eyes, wobbling as it breathes and hops. */
function slime(kind: Kind, glow: N): EnemyModel {
  const group = new THREE.Group();
  const body = new THREE.Group();
  body.userData.moves = true;
  group.add(body);
  const r = kind.radius;
  const b = -kind.height / 2;
  const gel = surface(kind.color, 'slime', glow);
  add(
    body,
    lathe(
      [
        [0.001, b],
        [r * 0.8, b],
        [r * 1.06, b + r * 0.16],
        [r * 1.1, b + r * 0.5],
        [r * 0.97, b + r * 1.08],
        [r * 0.72, b + r * 1.55],
        [r * 0.4, b + r * 1.84],
        [0.001, b + r * 1.92],
      ],
      1,
      28,
    ),
    gel,
  );
  // Bubbles caught just under the skin.
  const bubble = surface(new THREE.Color(kind.color).offsetHSL(0.02, -0.1, 0.18), 'slime', glow);
  for (const [x, y, z, s] of [
    [0.38, -0.25, -0.38, 0.07],
    [-0.45, -0.1, 0.3, 0.09],
    [0.2, 0.25, 0.45, 0.06],
    [-0.3, 0.32, -0.32, 0.05],
    [0.55, 0.0, 0.12, 0.06],
  ]) {
    add(body, new THREE.SphereGeometry(s, 10, 8), bubble, x, y, z);
  }
  // Eyes: big whites and pupils, and a small mouth.
  const white = surface(0xf2f0e6, 'dark', glow);
  white.roughness = 0.2;
  const black = surface(0x050505, 'dark', glow);
  for (const s of [-1, 1]) {
    add(body, new THREE.SphereGeometry(0.115, 14, 10), white, s * 0.17, 0.14, -0.47);
    add(body, new THREE.SphereGeometry(0.055, 10, 8), black, s * 0.165, 0.13, -0.565);
  }
  const mouth = add(body, new THREE.TorusGeometry(0.07, 0.016, 6, 12, Math.PI), black, 0, -0.05, -0.56);
  mouth.rotation.z = Math.PI;
  let clock = Math.random() * 10;
  let bounce = 0;
  return {
    group,
    shield: null,
    update(dt, speed) {
      clock += dt;
      bounce += (Math.min(1, speed / 2) - bounce) * (1 - Math.exp(-dt * 6));
      // Squash and stretch: breathing, and a jelly wobble when it moves.
      const w = Math.sin(clock * 3.2) * 0.04 + Math.sin(clock * 9) * 0.07 * bounce;
      body.scale.set(1 - w * 0.5, 1 + w, 1 - w * 0.5);
      body.position.y = -kind.height * 0.5 * w * 0.4;
    },
  };
}

/** A beetle: a shining shell, a horned head, six legs scuttling. */
function critter(kind: Kind, glow: N): EnemyModel {
  const group = new THREE.Group();
  const shell = surface(kind.color, 'shell', glow);
  const dark = surface(0x1a1420, 'shell', glow);
  const bone = surface(0xc9b98f, 'horn', glow);
  const b = -kind.height / 2;
  const body = new THREE.Group();
  body.userData.moves = true;
  body.position.y = b + 0.17;
  group.add(body);
  add(body, new THREE.SphereGeometry(0.13, 16, 12), shell, 0, 0, 0.05).scale.set(1.05, 0.72, 1.4);
  // The seam between the wing cases.
  add(body, new THREE.BoxGeometry(0.008, 0.012, 0.32), dark, 0, 0.092, 0.05);
  add(body, new THREE.SphereGeometry(0.075, 12, 10), dark, 0, 0.0, -0.13).scale.set(1.15, 0.8, 0.9);
  add(body, new THREE.SphereGeometry(0.055, 12, 10), dark, 0, -0.01, -0.2);
  // Mandibles and a horn.
  for (const s of [-1, 1]) {
    add(
      body,
      horn(
        [
          new THREE.Vector3(s * 0.03, -0.02, -0.23),
          new THREE.Vector3(s * 0.045, -0.03, -0.28),
          new THREE.Vector3(s * 0.01, -0.03, -0.31),
        ],
        0.012,
      ),
      bone,
    );
    const ant = add(
      body,
      new THREE.CylinderGeometry(0.003, 0.004, 0.12, 4).translate(0, 0.06, 0),
      dark,
      s * 0.025,
      0.03,
      -0.22,
    );
    ant.rotation.set(-0.9, 0, -s * 0.5);
  }
  add(
    body,
    horn(
      [
        new THREE.Vector3(0, 0.02, -0.19),
        new THREE.Vector3(0, 0.07, -0.24),
        new THREE.Vector3(0, 0.11, -0.22),
      ],
      0.016,
    ),
    bone,
  );
  const eye = surface(0x9cff6a, 'eye', glow);
  for (const s of [-1, 1]) add(body, new THREE.SphereGeometry(0.012, 6, 5), eye, s * 0.04, 0.01, -0.235);
  // Legs: from the body out to a knee, then down to the ground; they pivot at the body.
  const legs: THREE.Group[] = [];
  for (const z of [-0.12, -0.03, 0.07]) {
    for (const s of [-1, 1]) {
      const pivot = new THREE.Group();
      pivot.userData.moves = true;
      pivot.position.set(s * 0.07, 0, z);
      body.add(pivot);
      const knee = new THREE.Vector3(s * 0.12, 0.05, z * 0.4);
      const tip = new THREE.Vector3(s * 0.2, b - body.position.y + 0.01, z * 0.9);
      const a = add(pivot, new THREE.CylinderGeometry(0.009, 0.012, 1, 5).translate(0, 0.5, 0), dark);
      placeBone(a, new THREE.Vector3(), knee);
      const c = add(pivot, new THREE.CylinderGeometry(0.004, 0.009, 1, 5).translate(0, 0.5, 0), dark);
      placeBone(c, knee, tip);
      legs.push(pivot);
    }
  }
  let phase = Math.random() * 6;
  let busy = 0;
  return {
    group,
    shield: null,
    update(dt, speed) {
      busy += (Math.min(1, speed / 1.5) - busy) * (1 - Math.exp(-dt * 10));
      phase += dt * (2 + speed * 9);
      // A tripod gait: front and back on one side with the middle on the other.
      for (const [i, l] of legs.entries()) {
        const side = i % 2;
        const pair = Math.floor(i / 2);
        const t = phase + ((side + pair) % 2) * Math.PI;
        l.rotation.y = Math.sin(t) * 0.45 * busy;
        l.position.y = Math.max(0, Math.cos(t)) * 0.02 * busy;
      }
      body.rotation.z = Math.sin(phase * 2) * 0.03 * busy;
    },
  };
}

/** A knight in plate, a tabard and a crested helm, with a sword and a heater shield. */
function knight(kind: Kind, glow: N): EnemyModel {
  const group = new THREE.Group();
  const steel = surface(0xa3abb6, 'metal', glow);
  const darkSteel = surface(0x5d636c, 'metal', glow);
  const tabard = surface(0x2b4a86, 'cloth', glow);
  const red = surface(0x9a2a24, 'cloth', glow);
  const leather = surface(0x4a3020, 'leather', glow);
  const dark = surface(0x07070a, 'dark', glow);
  const b = -kind.height / 2;
  const hip = b + 0.9;
  const legs = [-1, 1].map((s) =>
    leg(group, s * 0.11, hip, 0.44, 0.42, [0.085, 0.068, 0.056], { thigh: steel, shin: steel }, (at) => {
      add(at, new RoundedBoxGeometry(0.11, 0.07, 0.25, 2, 0.025), darkSteel, 0, -0.01, -0.05);
    }),
  );
  const torso = new THREE.Group();
  torso.position.y = hip;
  group.add(torso);
  // Faulds over the hips, the cuirass, a belt, the tabard over it, and the gorget.
  add(
    torso,
    lathe(
      [
        [0.2, 0.08],
        [0.22, -0.04],
        [0.25, -0.2],
        [0.24, -0.22],
      ],
      0.82,
    ),
    darkSteel,
  );
  add(
    torso,
    lathe(
      [
        [0.2, 0.0],
        [0.205, 0.14],
        [0.255, 0.33],
        [0.265, 0.45],
        [0.2, 0.56],
        [0.08, 0.6],
        [0.001, 0.6],
      ],
      0.76,
    ),
    steel,
  );
  add(torso, new THREE.CylinderGeometry(0.212, 0.212, 0.05, 18), leather, 0, 0.03, 0).scale.z = 0.8;
  add(torso, new THREE.BoxGeometry(0.3, 0.74, 0.012), tabard, 0, 0.1, -0.205);
  add(torso, new THREE.BoxGeometry(0.3, 0.74, 0.012), tabard, 0, 0.1, 0.205);
  // A red cross on the tabard.
  add(torso, new THREE.BoxGeometry(0.05, 0.3, 0.014), red, 0, 0.26, -0.209);
  add(torso, new THREE.BoxGeometry(0.2, 0.05, 0.014), red, 0, 0.3, -0.209);
  add(torso, new THREE.CylinderGeometry(0.095, 0.12, 0.08, 14), darkSteel, 0, 0.6, 0);
  for (const s of [-1, 1]) {
    const pad = add(
      torso,
      new THREE.SphereGeometry(0.12, 14, 10, 0, Math.PI * 2, 0, Math.PI / 2),
      steel,
      s * 0.27,
      0.48,
      0,
    );
    pad.scale.set(1.05, 0.85, 1);
    pad.rotation.z = -s * 0.35;
  }
  // The helm: a great helm with a visor slit, rivets, and a red crest.
  const head = new THREE.Group();
  head.position.y = 0.73;
  torso.add(head);
  add(head, new THREE.CylinderGeometry(0.125, 0.13, 0.24, 18), steel);
  add(head, new THREE.SphereGeometry(0.125, 18, 10, 0, Math.PI * 2, 0, Math.PI / 2), steel, 0, 0.12, 0);
  add(head, new THREE.BoxGeometry(0.19, 0.024, 0.04), dark, 0, 0.035, -0.118);
  add(head, new THREE.BoxGeometry(0.018, 0.13, 0.03), darkSteel, 0, -0.04, -0.125);
  add(head, new THREE.SphereGeometry(0.05, 10, 8), red, 0, 0.21, 0.02).scale.set(0.45, 1.3, 2.6);
  // Sword arm: raised a little, the blade forward.
  const right = arm(
    group,
    [0.29, hip + 0.47, 0],
    [0.24, hip + 0.12, -0.3],
    0.29,
    0.27,
    [0.5, -0.6, 0.6],
    [0.062, 0.055, 0.048],
    {
      upper: steel,
      lower: steel,
      hand: darkSteel,
    },
  );
  const sword = buildWeaponModel(WEAPONS.find((w) => w.id === 'sword')!, weaponMats());
  sword.quaternion.setFromUnitVectors(_s.set(0, 1, 0), _h.set(0.1, 0.55, -1).normalize());
  sword.position.copy(right.position);
  group.add(sword);
  // The shield and the arm that holds it, which turn with it round the body.
  const shield = new THREE.Group();
  arm(
    shield,
    [-0.29, hip + 0.47, 0],
    [-0.1, hip + 0.15, -0.42],
    0.29,
    0.27,
    [-0.6, -0.5, 0.4],
    [0.062, 0.055, 0.048],
    {
      upper: steel,
      lower: steel,
      hand: darkSteel,
    },
  );
  const heater = new THREE.Shape();
  const W = 0.36;
  const H = 0.5;
  heater.moveTo(-W, H);
  heater.lineTo(W, H);
  heater.lineTo(W, 0);
  heater.quadraticCurveTo(W, -H * 0.7, 0, -H);
  heater.quadraticCurveTo(-W, -H * 0.7, -W, 0);
  heater.closePath();
  const face = new THREE.ExtrudeGeometry(heater, {
    depth: 0.03,
    bevelEnabled: true,
    bevelSize: 0.02,
    bevelThickness: 0.012,
    bevelSegments: 2,
    curveSegments: 10,
  });
  face.rotateY(Math.PI);
  add(shield, face, tabard, -0.02, hip + 0.18, -kind.radius - 0.1);
  // A rim, and the cross again.
  const rim = new THREE.ExtrudeGeometry(heater, { depth: 0.02, bevelEnabled: false, curveSegments: 10 });
  rim.scale(1.08, 1.06, 1);
  rim.rotateY(Math.PI);
  add(shield, rim, darkSteel, -0.02, hip + 0.18, -kind.radius - 0.09);
  add(shield, new THREE.BoxGeometry(0.08, 0.66, 0.01), red, -0.02, hip + 0.14, -kind.radius - 0.165);
  add(shield, new THREE.BoxGeometry(0.5, 0.08, 0.01), red, -0.02, hip + 0.3, -kind.radius - 0.165);
  group.add(shield);
  const gait = new Gait(legs, torso, 0.45);
  return { group, shield, update: (dt, speed) => gait.update(dt, speed) };
}

/** A raider: hooded and masked, in leather, a curved blade in each hand. */
function raider(kind: Kind, glow: N): EnemyModel {
  const group = new THREE.Group();
  const leather = surface(kind.color, 'leather', glow);
  const darkLeather = surface(0x3a2618, 'leather', glow);
  const cloth = surface(0x2e2b33, 'cloth', glow);
  const sash = surface(0x8a2b22, 'cloth', glow);
  const shadow = surface(0x050506, 'dark', glow);
  const steel = surface(0xb8bec6, 'metal', glow);
  const b = -kind.height / 2;
  const hip = b + 0.86;
  const legs = [-1, 1].map((s) =>
    leg(group, s * 0.1, hip, 0.43, 0.41, [0.075, 0.06, 0.05], { thigh: cloth, shin: cloth }, (at) => {
      add(at, new THREE.CylinderGeometry(0.06, 0.055, 0.16, 10), darkLeather, 0, 0.06, 0);
      add(at, new RoundedBoxGeometry(0.09, 0.06, 0.22, 2, 0.025), darkLeather, 0, -0.01, -0.05);
    }),
  );
  const torso = new THREE.Group();
  torso.position.y = hip;
  group.add(torso);
  add(
    torso,
    lathe(
      [
        [0.001, -0.02],
        [0.17, 0.0],
        [0.175, 0.15],
        [0.215, 0.35],
        [0.22, 0.45],
        [0.16, 0.54],
        [0.06, 0.58],
        [0.001, 0.58],
      ],
      0.7,
    ),
    leather,
  );
  // The sash, with its tail.
  add(torso, new THREE.CylinderGeometry(0.185, 0.18, 0.08, 16), sash, 0, 0.04, 0).scale.z = 0.75;
  const tail = add(torso, new THREE.BoxGeometry(0.06, 0.3, 0.01), sash, 0.12, -0.1, -0.12);
  tail.rotation.set(0.15, -0.6, 0.15);
  // Straps across the chest, and a mantle round the shoulders.
  const strap = add(torso, new THREE.BoxGeometry(0.04, 0.6, 0.012), darkLeather, 0, 0.3, -0.155);
  strap.rotation.z = 0.6;
  add(
    torso,
    lathe(
      [
        [0.1, 0.62],
        [0.24, 0.5],
        [0.29, 0.36],
        [0.28, 0.33],
      ],
      0.82,
    ),
    cloth,
  );
  const cape = new THREE.CylinderGeometry(0.22, 0.3, 0.8, 14, 2, true, -Math.PI * 0.4, Math.PI * 0.8);
  cape.translate(0, -0.4, 0);
  const capeMesh = add(torso, cape, cloth, 0, 0.5, 0.03);
  capeMesh.scale.z = 0.75;
  const capeMat = cloth.clone();
  capeMat.side = THREE.DoubleSide;
  capeMesh.material = capeMat;
  // The hooded head: a shadowed face, a mask over the mouth, eyes catching the light.
  const head = new THREE.Group();
  head.position.y = 0.7;
  torso.add(head);
  add(head, new THREE.SphereGeometry(0.105, 14, 12), shadow).scale.set(0.9, 1.05, 1);
  const hood = add(
    head,
    new THREE.SphereGeometry(0.135, 18, 14, Math.PI * 1.22, Math.PI * 1.56, 0, Math.PI * 0.72),
    cloth,
    0,
    0.02,
    0.01,
  );
  hood.scale.set(1, 1.08, 1.08);
  const peak = add(head, new THREE.ConeGeometry(0.06, 0.12, 8), cloth, 0, 0.13, 0.05);
  peak.rotation.x = 0.7;
  add(
    head,
    new THREE.CylinderGeometry(0.1, 0.09, 0.09, 14, 1, true, -Math.PI * 0.55, Math.PI * 1.1),
    cloth,
    0,
    -0.05,
    0,
  );
  const eye = surface(0xffb04a, 'eye', glow);
  for (const s of [-1, 1]) add(head, new THREE.SphereGeometry(0.012, 6, 5), eye, s * 0.035, 0.015, -0.095);
  // Arms, and a curved knife in each hand, held low and forward.
  for (const s of [-1, 1]) {
    const hand = arm(
      group,
      [s * 0.22, hip + 0.48, 0],
      [s * 0.22, hip + 0.08, -0.28],
      0.28,
      0.27,
      [s * 0.5, -0.5, 0.6],
      [0.055, 0.048, 0.042],
      {
        upper: leather,
        lower: darkLeather,
        hand: darkLeather,
      },
    );
    const knife = new THREE.Group();
    knife.position.copy(hand.position);
    knife.quaternion.setFromUnitVectors(_s.set(0, 1, 0), _h.set(s * 0.25, 0.4, -1).normalize());
    add(knife, new THREE.CylinderGeometry(0.014, 0.014, 0.1, 8), darkLeather);
    add(knife, new THREE.BoxGeometry(0.07, 0.014, 0.022), steel, 0, 0.055, 0);
    add(
      knife,
      horn(
        [new THREE.Vector3(0, 0.06, 0), new THREE.Vector3(0.02, 0.2, 0), new THREE.Vector3(0.06, 0.32, 0)],
        0.022,
      ),
      steel,
    ).scale.z = 0.35;
    group.add(knife);
  }
  const gait = new Gait(legs, torso, 0.55, 1.9);
  return { group, shield: null, update: (dt, speed) => gait.update(dt, speed) };
}

/** A charger: a hunched, horned bull of a beast on hooves, a ring through its nose. */
function charger(kind: Kind, glow: N): EnemyModel {
  const group = new THREE.Group();
  const fur = surface(kind.color, 'skin', glow);
  const mane = surface(new THREE.Color(kind.color).multiplyScalar(0.35), 'cloth', glow);
  const muzzle = surface(0x3a2520, 'skin', glow);
  const ivory = surface(0xd9cdb0, 'horn', glow);
  const hoof = surface(0x15100e, 'dark', glow);
  const brass = surface(0xb08a3c, 'metal', glow);
  const dark = surface(0x050505, 'dark', glow);
  const b = -kind.height / 2;
  const hip = b + 0.84;
  const legs = [-1, 1].map((s) =>
    leg(group, s * 0.17, hip, 0.42, 0.38, [0.13, 0.095, 0.07], { thigh: fur, shin: fur }, (at) => {
      add(at, new THREE.CylinderGeometry(0.07, 0.085, 0.09, 10), hoof, 0, 0.0, -0.02);
    }),
  );
  const torso = new THREE.Group();
  torso.position.y = hip;
  torso.rotation.x = -0.28;
  group.add(torso);
  add(
    torso,
    lathe(
      [
        [0.001, -0.04],
        [0.22, 0.0],
        [0.27, 0.15],
        [0.37, 0.4],
        [0.41, 0.55],
        [0.32, 0.7],
        [0.13, 0.78],
        [0.001, 0.8],
      ],
      0.8,
    ),
    fur,
  );
  // A shaggy mane over the shoulders and down the back.
  for (let i = 0; i < 9; i++) {
    const a = (i / 8 - 0.5) * 2.4;
    const tuft = add(
      torso,
      new THREE.ConeGeometry(0.08, 0.26, 6),
      mane,
      Math.sin(a) * 0.26,
      0.66 - Math.abs(a) * 0.08,
      0.1 + Math.cos(a) * 0.12,
    );
    tuft.rotation.set(0.9, 0, -Math.sin(a) * 0.8);
  }
  // A loincloth.
  add(torso, new THREE.BoxGeometry(0.22, 0.3, 0.01), mane, 0, -0.12, -0.2);
  // The head, thrust forward: skull, muzzle, nostrils and ring, glowering eyes, ears and horns.
  const head = new THREE.Group();
  head.position.set(0, 0.84, -0.16);
  head.rotation.x = 0.28;
  head.userData.moves = true;
  torso.add(head);
  add(head, new THREE.SphereGeometry(0.16, 16, 12), fur).scale.set(1, 0.92, 1.15);
  add(head, new THREE.SphereGeometry(0.11, 14, 10), muzzle, 0, -0.07, -0.17).scale.set(1.05, 0.8, 1.1);
  for (const s of [-1, 1]) add(head, new THREE.SphereGeometry(0.018, 6, 5), dark, s * 0.04, -0.06, -0.285);
  const ring = add(head, new THREE.TorusGeometry(0.035, 0.007, 6, 14), brass, 0, -0.11, -0.28);
  ring.rotation.x = 0.3;
  const eye = surface(0xff5a1e, 'eye', glow);
  for (const s of [-1, 1]) {
    add(head, new THREE.SphereGeometry(0.02, 8, 6), eye, s * 0.085, 0.03, -0.14);
    const ear = add(head, new THREE.ConeGeometry(0.035, 0.1, 6), fur, s * 0.15, 0.04, 0.02);
    ear.rotation.z = s * -1.6;
    add(
      head,
      horn(
        [
          new THREE.Vector3(s * 0.12, 0.08, -0.02),
          new THREE.Vector3(s * 0.27, 0.1, -0.04),
          new THREE.Vector3(s * 0.36, 0.22, -0.12),
          new THREE.Vector3(s * 0.33, 0.36, -0.22),
        ],
        0.045,
      ),
      ivory,
    );
  }
  // Heavy arms, fists low and forward.
  for (const s of [-1, 1]) {
    arm(
      group,
      [s * 0.36, hip + 0.5, -0.08],
      [s * 0.36, hip + 0.02, -0.32],
      0.32,
      0.3,
      [s * 0.5, -0.4, 0.7],
      [0.1, 0.085, 0.07],
      {
        upper: fur,
        lower: fur,
        hand: muzzle,
      },
    );
  }
  const gait = new Gait(legs, torso, 0.6, 1.4);
  let clock = 0;
  return {
    group,
    shield: null,
    update(dt, speed) {
      gait.update(dt, speed);
      clock += dt;
      // Snorting: the head tosses now and then.
      head.rotation.x = 0.28 + Math.max(0, Math.sin(clock * 1.3)) ** 8 * 0.25;
    },
  };
}

/** A brute: a hulking, tusked ogre with a studded club over its shoulder. */
function brute(kind: Kind, glow: N): EnemyModel {
  const group = new THREE.Group();
  const skin = surface(0x6c7663, 'skin', glow);
  const leather = surface(0x4b3424, 'leather', glow);
  const iron = surface(0x4c4f55, 'metal', glow);
  const wood = surface(0x5a3d26, 'leather', glow);
  const bone = surface(0xd6c9a6, 'horn', glow);
  const dark = surface(0x0a0a0a, 'dark', glow);
  const b = -kind.height / 2;
  const hip = b + 1.2;
  const legs = [-1, 1].map((s) =>
    leg(group, s * 0.28, hip, 0.62, 0.55, [0.23, 0.18, 0.15], { thigh: skin, shin: skin }, (at) => {
      add(at, new RoundedBoxGeometry(0.26, 0.13, 0.38, 2, 0.05), skin, 0, -0.0, -0.07);
      add(at, new THREE.CylinderGeometry(0.16, 0.15, 0.1, 12), leather, 0, 0.12, 0);
    }),
  );
  const torso = new THREE.Group();
  torso.position.y = hip;
  group.add(torso);
  // A loincloth and belt, a great belly, a barrel chest and hunched shoulders.
  add(
    torso,
    lathe(
      [
        [0.4, 0.1],
        [0.45, -0.05],
        [0.5, -0.38],
        [0.48, -0.4],
      ],
      0.8,
      18,
    ),
    leather,
  );
  add(torso, new THREE.CylinderGeometry(0.47, 0.47, 0.1, 20), leather, 0, 0.1, 0).scale.z = 0.8;
  add(torso, new RoundedBoxGeometry(0.16, 0.12, 0.04, 2, 0.02), iron, 0, 0.1, -0.38);
  add(
    torso,
    lathe(
      [
        [0.001, 0.05],
        [0.42, 0.06],
        [0.52, 0.3],
        [0.63, 0.72],
        [0.67, 0.98],
        [0.52, 1.16],
        [0.22, 1.27],
        [0.001, 1.3],
      ],
      0.74,
      24,
    ),
    skin,
  );
  add(torso, new THREE.SphereGeometry(0.5, 20, 14), skin, 0, 0.42, -0.1).scale.set(1, 0.95, 0.85);
  for (const s of [-1, 1]) add(torso, new THREE.SphereGeometry(0.24, 14, 10), skin, s * 0.6, 1.08, 0.02);
  // A strap across the chest with an iron plate on it.
  const strap = add(torso, new THREE.BoxGeometry(0.08, 1.2, 0.02), leather, 0.05, 0.75, -0.36);
  strap.rotation.z = -0.65;
  add(
    torso,
    new THREE.SphereGeometry(0.17, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2),
    iron,
    -0.5,
    1.15,
    0.0,
  ).rotation.z = 0.5;
  // The head: low between the shoulders, a heavy brow, small eyes, an underbite and tusks.
  const head = new THREE.Group();
  head.position.set(0, 1.42, -0.16);
  torso.add(head);
  add(head, new THREE.SphereGeometry(0.22, 16, 12), skin).scale.set(1, 0.92, 1);
  add(head, new THREE.SphereGeometry(0.2, 14, 10), skin, 0, -0.1, -0.06).scale.set(1.1, 0.75, 1);
  add(head, new RoundedBoxGeometry(0.34, 0.07, 0.1, 2, 0.03), skin, 0, 0.06, -0.17);
  const eye = surface(0xffd23a, 'eye', glow);
  for (const s of [-1, 1]) {
    add(head, new THREE.SphereGeometry(0.022, 8, 6), eye, s * 0.075, 0.02, -0.2);
    add(
      head,
      horn(
        [
          new THREE.Vector3(s * 0.09, -0.14, -0.2),
          new THREE.Vector3(s * 0.11, -0.06, -0.25),
          new THREE.Vector3(s * 0.1, 0.0, -0.24),
        ],
        0.022,
      ),
      bone,
    );
    add(head, new THREE.SphereGeometry(0.05, 8, 6), skin, s * 0.21, 0.0, 0.0).scale.set(0.5, 1.2, 0.8);
  }
  add(head, new THREE.SphereGeometry(0.035, 8, 6), dark, 0, -0.04, -0.235).scale.set(1.3, 0.7, 0.8);
  // Arms: the right holds the club over its shoulder, the left hangs heavy.
  const right = arm(
    group,
    [0.66, hip + 1.02, 0],
    [0.6, hip + 0.42, -0.36],
    0.6,
    0.55,
    [0.6, -0.5, 0.5],
    [0.17, 0.145, 0.12],
    {
      upper: skin,
      lower: skin,
      hand: skin,
    },
  );
  arm(
    group,
    [-0.66, hip + 1.02, 0],
    [-0.74, hip + 0.02, -0.12],
    0.6,
    0.55,
    [-0.6, 0.0, 0.8],
    [0.17, 0.145, 0.12],
    {
      upper: skin,
      lower: skin,
      hand: skin,
    },
  );
  const club = new THREE.Group();
  club.position.copy(right.position);
  club.quaternion.setFromUnitVectors(_s.set(0, 1, 0), _h.set(-0.15, 1, 0.75).normalize());
  add(club, new THREE.CylinderGeometry(0.13, 0.05, 1.35, 12).translate(0, 0.55, 0), wood);
  add(club, new THREE.CylinderGeometry(0.135, 0.135, 0.06, 12), iron, 0, 1.05, 0);
  for (let i = 0; i < 10; i++) {
    const a = i * 2.4;
    const y = 0.75 + (i % 4) * 0.1;
    const r = 0.06 + ((y - 0.1) / 1.35) * 0.08;
    const stud = add(club, new THREE.ConeGeometry(0.025, 0.07, 5), iron, Math.cos(a) * r, y, Math.sin(a) * r);
    stud.quaternion.setFromUnitVectors(_s.set(0, 1, 0), _h.set(Math.cos(a), 0, Math.sin(a)));
  }
  group.add(club);
  const gait = new Gait(legs, torso, 0.35, 1.1);
  let clock = Math.random() * 10;
  return {
    group,
    shield: null,
    update(dt, speed) {
      gait.update(dt, speed);
      clock += dt;
      // Heavy breathing.
      torso.scale.set(1 + Math.sin(clock * 1.2) * 0.012, 1, 1 + Math.sin(clock * 1.2) * 0.02);
    },
  };
}
